import { useEffect, useState } from "react";

/**
 * Global lightbox: any <img> on the page opens full screen when clicked.
 * Images opted out with data-no-lightbox are ignored.
 */
export function ImageLightbox() {
  const [image, setImage] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const img = target?.closest?.("img") as HTMLImageElement | null;
      if (!img || img.dataset["noLightbox"] !== undefined) return;
      if (target?.closest("a")) return;
      setImage({ src: img.currentSrc || img.src, alt: img.alt || "" });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setImage(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = image ? "hidden" : "";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [image]);

  if (!image) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.alt || "Image"}
      onClick={() => setImage(null)}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-6 backdrop-blur-sm md:p-12"
    >
      <img
        src={image.src}
        alt={image.alt}
        data-no-lightbox
        className="max-h-full max-w-full object-contain"
      />
      <button
        type="button"
        aria-label="Close"
        onClick={() => setImage(null)}
        className="fixed right-5 top-5 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60 transition-colors hover:text-bone md:right-[3vw] md:top-8"
      >
        Close
      </button>
    </div>
  );
}
